import { formatLatLon, getFacilityNameByRadius } from "../../../../utils/lat_lon_utils";
import { isEmpty } from "lodash";
import { parseUserInfo } from "../utils";
import { unix_utc_to_local_12_hour_clock } from "../../../../utils/date_time_utils";
import { useTranslation as UseTranslation } from "react-i18next";
import modifyColumns from "../../../../utils/modifyColumns";
import getColumnType from "../../../../utils/get_column_type";

export default function columns(props) {
  const {
    classifications = [],
    columnOrder = [],
    customs = [],
    facilities = {},
    history = {},
    parentAssetIdTagMap = {},
    timeZone = '',
    useTranslation = true,
    zones = {},
  } = props;

  const { t = () => { } } = useTranslation ? UseTranslation() : () => { };

  const classificationColumns = !isEmpty(classifications)
    ? Object.keys(classifications).map((classKey) => {
      const { name } = classifications[classKey];

      return {
        field: name,
        headerClassName: "datagrid-header",
        headerName: name,
        id: name,
        minWidth: 100,
        flex: 1,
        renderCell: (data) => {
          if (data.rowNode.type === "group") return data.value;
          const { row } = data;
          const { classificationMap = {} } = row;

          const classificationValue = Object.keys(classificationMap).filter(
            (k) => k === name
          )[0];

          return (
            <div className="pointer" key={name} onClick={() => onClick(row)}>
              {classificationMap[classificationValue] || t("dnpinput1")}
            </div>
          );
        },
        valueGetter: (value, row) => {
          if (row.classificationMap) {
            const classificationValue = row.classificationMap[classKey] || "";
            return classificationValue;
          }
          return "";
        },
        groupingValueGetter: (value, row) => {
          if (row.classificationMap) {
            const classificationValue = row.classificationMap[classKey] || "";
            return classificationValue;
          }
          return null;
        },
      };
    })
    : [];

  const customColumns = !isEmpty(customs)
    ? customs.map((item) => {
      const { dataType, name } = item;

      const colType = getColumnType(dataType);

      return {
        field: name,
        headerClassName: "datagrid-header",
        headerName: name,
        id: name,
        minWidth: 100,
        flex: 1,
        renderCell: (data) => {
          // Check if the row type is "group" or "pinnedRow", and return formatted date or fallback text accordingly.
          if (["group", "pinnedRow"].includes(data.rowNode.type)) {
            return colType === "date" && data.value
              ? new Date(data.value).toLocaleDateString() || t("dnpinput1")
              : data.value || t("dnpinput1");
          }
          const { row } = data;
          const { customMap = {} } = row;

          const customValue = Object.keys(customMap).filter(
            (k) => k === name
          )[0];
          const renderValue = customMap[customValue];

          return (
            <div className="pointer" key={name} onClick={() => onClick(row)}>
              {/* //if colType is date and returnVal is not undefined, return the date */}
              {colType === "date" && renderValue !== undefined
                ? new Date(renderValue).toLocaleDateString()
                : renderValue || t("dnpinput1")}
            </div>
          );
        },
        type: colType,
        valueGetter: (value, row) => {
          const { customMap = {} } = row;
          const customValue = Object.keys(customMap).filter(
            (k) => k === name
          )[0];
          const returnVal = customMap[customValue];
          //if column is a number, return the value as an integer
          if (colType === "number") {
            return parseInt(returnVal) || null;
          }
          if (colType === "date") {
            return returnVal !== undefined ? new Date(returnVal) : null;
          }
          return returnVal || null;
        },
        groupingValueGetter: (value, row) => {
          const { customMap = {} } = row;
          const customValue = Object.keys(customMap).filter(
            (k) => k === name
          )[0];
          const returnVal = customMap[customValue];
          if (colType === "number") {
            return parseInt(returnVal) || null;
          }
          if (colType === "date") {
            return returnVal !== undefined ? new Date(returnVal) : null;
          }
          return returnVal || null;
        },
      };
    })
    : [];

  const historyTableColumns = [
    // timeOfLog
    {
      field: "timeOfLog",
      groupable: false,
      headerClassName: "datagrid-header",
      headerName: "Date/Time",
      id: "timeOfLog",
      renderCell: (data) => {
        const { row } = data;
        const { timeOfLogLong } = row;

        return (
          <div className="pointer" onClick={() => onClick(row)}>
            {timeOfLogLong
              ? unix_utc_to_local_12_hour_clock(timeOfLogLong, timeZone)
              : t("dnpinput1")}
          </div>
        );
      },
      type: "dateTime",
      valueGetter: (value, row) => {
        const timeOfLogLong = row?.timeOfLogLong;
        return timeOfLogLong
          ? new Date(timeOfLogLong)
          : //furthest date in the future. We want records without a timeOfLogLong to be at the bottom of the table in case of "newest to oldest" sort
          new Date(8640000000000000);
      },
      minWidth: 180,
      flex: 1,
    },
    // tag
    {
      field: "tag",
      headerClassName: "datagrid-header",
      headerName: "Inventory Tag",
      id: "tag",
      renderCell: (data) => {
        if (data.rowNode.type === "group") return data.value;
        const { row } = data;
        const { tag } = row;
        return (
          <div className="pointer" onClick={() => onClick(row)}>
            {tag ? tag : t("dnpinput1")}
          </div>
        );
      },
      valueGetter: (value, row) => {
        const { tag } = row;
        return tag ? tag : t("dnpinput1");
      },
      groupingValueGetter: (value, row) => {
        const { tag } = row;
        return tag ? tag : null;
      },
      minWidth: 120,
      flex: 1,
    },
    // category
    {
      field: "category",
      headerClassName: "datagrid-header",
      headerName: "Inventory Category",
      id: "category",
      renderCell: (data) => {
        if (data.rowNode.type === "group") return data.value;
        const { row } = data;
        const { category } = row;
        return (
          <div className={"pointer"} onClick={() => onClick(row)}>
            {category ? category : t("dnpinput1")}
          </div>
        );
      },
      valueGetter: (value, row) =>
        row.category ? row.category : t("dnpinput1"),
      groupingValueGetter: (value, row) => (row.category ? row.category : null),
      minWidth: 120,
      flex: 1,
    },
    // description
    {
      field: "description",
      headerClassName: "datagrid-header",
      headerName: "Description",
      id: "description",
      renderCell: (data) => {
        if (data.rowNode.type === "group") return data.value;
        const { row } = data;
        const { propertiesMap = {} } = row;
        return (
          <div
            className="pointer"
            style={{
              display: "flex",
              alignItems: "center",
            }}
            onClick={() => onClick(row)}
          >
            {propertiesMap?.description || t("dnpinput1")}
          </div>
        );
      },
      valueGetter: (value, row) => row.propertiesMap?.description, // required for nested values
      groupingValueGetter: (value, row) =>
        row.propertiesMap?.description || null, // required for nested values
      minWidth: 120,
      flex: 1,
    },
    // lastEvent
    {
      field: "lastEvent",
      headerClassName: "datagrid-header",
      headerName: "Last Event",
      id: "lastEvent",
      renderCell: (data) => {
        if (data.rowNode.type === "group") return data.value;
        const { row } = data;
        const { lastEvent } = row;
        return (
          <div
            className="pointer"
            style={{
              display: "flex",
              alignItems: "center",
            }}
            onClick={() => onClick(row)}
          >
            {lastEvent ? lastEvent : t("dnpinput1")}
          </div>
        );
      },
      valueGetter: (value, row) => {
        const { lastEvent } = row;
        return lastEvent ? lastEvent : t("dnpinput1");
      },
      groupingValueGetter: (value, row) => {
        const { lastEvent } = row;
        return lastEvent ? lastEvent : null;
      },
      minWidth: 100,
      flex: 1,
    },
    // user
    {
      field: "user",
      headerClassName: "datagrid-header",
      headerName: "User",
      id: "user",
      renderCell: (data) => {
        if (data.rowNode.type === "group") return data.value;
        const { row } = data;
        const userInfo = parseUserInfo(row);
        return (
          <div
            className="pointer"
            style={{
              display: "flex",
              alignItems: "center",
            }}
            onClick={() => onClick(row)}
          >
            {userInfo || t("dnpinput1")}
          </div>
        );
      },
      valueGetter: (value, row) => parseUserInfo(row), // required for nested values
      groupingValueGetter: (value, row) => parseUserInfo(row) || null,
      minWidth: 100,
      flex: 1,
    },
    // facility - determined by checking if event coordinates are within facility radius
    {
      field: "facility",
      headerClassName: "datagrid-header",
      headerName: "Facility Name",
      id: "facility",
      renderCell: (data) => {
        if (data.rowNode.type === "group") return data.value;
        const { row } = data;
        const { latitude, longitude } = row;
        
        // Check if we have valid GPS coordinates
        const hasGpsCoordinates = latitude && longitude && (latitude !== 0 || longitude !== 0);
        
        // Determine facility based on radius matching
        let facilityName = "-";
        if (hasGpsCoordinates) {
          facilityName = getFacilityNameByRadius(latitude, longitude, facilities);
        } else {
          facilityName = t("dnpinput1");
        }
        
        return (
          <div className={"pointer"} onClick={() => onClick(row)}>
            {facilityName}
          </div>
        );
      },
      valueGetter: (value, row) => {
        const { latitude, longitude } = row;
        const hasGpsCoordinates = latitude && longitude && (latitude !== 0 || longitude !== 0);
        
        if (hasGpsCoordinates) {
          return getFacilityNameByRadius(latitude, longitude, facilities);
        }
        return t("dnpinput1");
      },
      groupingValueGetter: (value, row) => {
        const { latitude, longitude } = row;
        const hasGpsCoordinates = latitude && longitude && (latitude !== 0 || longitude !== 0);
        
        if (hasGpsCoordinates) {
          const name = getFacilityNameByRadius(latitude, longitude, facilities);
          return name !== "-" ? name : null;
        }
        return null;
      },
      minWidth: 100,
      flex: 1,
    },
    // latLong
    {
      field: "latLong",
      headerName: "Latitude, Longitude",
      id: "latLong",
      renderCell: (data) => {
        if (data.rowNode.type === "group") return data.value;
        const { row } = data;
        const { latitude, longitude } = row;

        const formattedLatLong =
          latitude && longitude
            ? formatLatLon(latitude, longitude)
            : t("dnpinput1");

        return (
          <div onClick={() => onClick(row)} key={row.assetId}>
            {formattedLatLong}
          </div>
        );
      },
      valueGetter: (value, row) => {
        const { latitude, longitude } = row;

        const formattedLatLong =
          latitude && longitude
            ? formatLatLon(latitude, longitude)
            : t("dnpinput1");

        return formattedLatLong;
      },
      groupingValueGetter: (value, row) => {
        const { latitude, longitude } = row;

        const formattedLatLong =
          latitude && longitude
            ? formatLatLon(latitude, longitude)
            : t("dnpinput1");

        return formattedLatLong || null;
      },
      minWidth: 130,
      flex: 1,
    },
    // zone
    {
      field: "zone",
      headerClassName: "datagrid-header",
      headerName: "Zone",
      id: "zone",
      renderCell: (data) => {
        if (data.rowNode.type === "group") return data.value;
        const { row } = data;
        const { zone = {} } = row;
        const { zoneName = "", pZoneName = "", zoneId = "" } = zone;

        return (
          <div className="pointer" onClick={() => onClick(row)}>
            {/* Show pZoneName. If not present show pZoneName. If not present the Zone Name */}
            {pZoneName
              ? pZoneName
              : zoneName
                ? zoneName
                : zoneId && zones[zoneId]
                  ? zones[zoneId].name
                  : t("dnpinput1")}
          </div>
        );
      },
      valueGetter: (value, row) => {
        const { zone = {} } = row;
        const { zoneName = "", pZoneName = "", zoneId = "" } = zone;

        return zoneName
          ? zoneName
          : pZoneName
            ? pZoneName
            : zoneId && zones[zoneId]
              ? zones[zoneId].name
              : t("dnpinput1");
      },
      groupingValueGetter: (value, row) => {
        const { zone = {} } = row;
        const { zoneName = "", pZoneName = "", zoneId = "" } = zone;

        return zoneName
          ? zoneName
          : pZoneName
            ? pZoneName
            : zoneId && zones[zoneId]
              ? zones[zoneId].name
              : null;
      },
      minWidth: 80,
      flex: 1,
    },
    // binLocation
    {
      field: "binLocation",
      headerClassName: "datagrid-header",
      headerName: "Bin Location",
      id: "binLocation",
      renderCell: (data) => {
        if (data.rowNode.type === "group") return data.value;
        const { row } = data;
        const { zone = {} } = row;
        const { binLocation = "" } = zone;
        return (
          <div className="pointer" onClick={() => onClick(row)}>
            {
              // We have to accomodate for older assets that have the BinLocation in propertiesMap
              // The first ternay checks to see if its on the main object. The second checks if its
              // in the propertiesMap. The third then checks for it in the zone type.
              row.zone
                ? binLocation
                : row.binLocation
                  ? row.binLocation
                  : row.propertiesMap && row.propertiesMap.binLocation
                    ? row.propertiesMap.binLocation
                    : t("dnpinput1")
            }
          </div>
        );
      },
      valueGetter: (value, row) => {
        const { zone = {} } = row;
        const { binLocation = "" } = zone;
        return row.zone
          ? binLocation
          : row.binLocation
            ? row.binLocation
            : row.propertiesMap && row.propertiesMap.binLocation
              ? row.propertiesMap.binLocation
              : t("dnpinput1");
      },
      groupingValueGetter: (value, row) => {
        const { zone = {} } = row;
        const { binLocation = "" } = zone;
        return row.zone
          ? binLocation
          : row.binLocation
            ? row.binLocation
            : row.propertiesMap && row.propertiesMap.binLocation
              ? row.propertiesMap.binLocation
              : null;
      },
      minWidth: 100,
      flex: 1,
    },
    // parentId
    {
      field: "parentId",
      headerClassName: "datagrid-header",
      headerName: "Parent Asset Tag",
      id: "parentId",
      renderCell: (data) => {
        if (data.rowNode.type === "group") return data.value;
        const { row } = data;

        const parent = row.ancestors ? row.ancestors[0] : null;
        const parentName = parent ? parentAssetIdTagMap[parent] : "";

        return (
          <div
            className="pointer"
            style={{
              display: "flex",
              alignItems: "center",
            }}
            onClick={() => onClick({ assetId: parent })}
          >
            {parentName}
          </div>
        );
      },
      valueGetter: (value, row) => {
        if (!row.ancestors) {
          return "";
        } else {
          // immediate parent is the first item in ancestors set, the child item's parent node in the hierarchy tree
          const parent = row.ancestors ? row.ancestors[0] : null;
          const parentName = parent ? parentAssetIdTagMap[parent] : "";

          return parentName;
        }
      },
      groupingValueGetter: (value, row) => {
        if (!row.ancestors) {
          return "";
        } else {
          // immediate parent is the first item in ancestors set, the child item's parent node in the hierarchy tree
          const parent = row.ancestors ? row.ancestors[0] : null;
          const parentName = parent ? parentAssetIdTagMap[parent] : null;

          return parentName;
        }
      },
      minWidth: 100,
      flex: 1,
    },
    // quantityCurrent
    {
      field: "quantityCurrent",
      headerClassName: "datagrid-header",
      headerName: "Qty",
      id: "quantityCurrent",
      renderCell: (data) => {
        if (data.rowNode.type === "group" || data.rowNode.type === "pinnedRow")
          return data.value;
        const { row } = data;
        return (
          <div className="pointer" onClick={() => onClick(row)}>
            {row.quantity?.quantityCurrent || t("dnpinput1")}
          </div>
        );
      },
      type: "number",
      valueGetter: (value, row) => {
        if (row.quantity?.quantityCurrent) {
          return parseInt(row.quantity.quantityCurrent);
        } else {
          return null;
        }
      },
      groupingValueGetter: (value, row) => {
        if (row.quantity?.quantityCurrent) {
          return parseInt(row.quantity.quantityCurrent);
        } else {
          return null;
        }
      },
      minWidth: 60,
      flex: 0.5,
    }
  ]
    .concat(classificationColumns)
    .filter((col) => {
      // get rid of event specific comments
      if (col.field.includes("Comments") && col.field !== "Comments") {
        return false;
      } else {
        return true;
      }
    })
    .concat(customColumns);

  //iterate over columns and if a column has a type of number, allow aggregation on that column
  // Number columns: aggregation + right-align for scannability
  historyTableColumns.forEach((column) => {
    if (column.type === "number") {
      column.aggregable = true;
      column.align = "right";
      column.headerAlign = "right";
    } else {
      column.aggregable = false;
    }
  });

  const onClick = (row) => {
    const { assetId = "" } = row;
    if (assetId) {
      const url = `/inventorySnapshot/${assetId}`;
      history.push(url);
    }
  };

  // Here we need to go through the columnOrder and handle Sorting / Renaming / Visibility settings
  if (columnOrder) {
    return modifyColumns({ columnOrder, historyTableColumns });
  }

  return { historyTableColumns };
}
